import PropTypes from "prop-types";
import {
	Container,
	Row,
	Col,
	Card
} from "reactstrap";
import { useState, useEffect, useReducer, useMemo, useContext } from "react";
import TreeNode from "./TreeNode";
import CreateEditOrganizationModalViewModel from "./CreateEditOrganizationModalViewModel";
import EditTenantViewModel from "./EditTenantViewModel";
import SimpleHeader from "../../components/SimpleHeader/SimpleHeader";
import { treeState, treeTypes } from "../../constants/treeState";
import { LocationModalContext } from "../../utilities/Contexts/LocationModalContext";
import StructuralHierarchyTree from "../../components/StructuralHierarchyTree/StructuralHierarchyTree";
import { StructuralAccountType } from "./StructuralAccountType";
import { removeNodesByType } from "../../utilities/HierarchyOperations/removeNodesByType";
import { ToastMessage } from '../../components/ToastMessage/ToastMessage.js';
import MainTreeHeader from "../../components/StructuralHierarchyTree/MainTreeHeader";
import LocationModalTreeHeader from "../../components/StructuralHierarchyTree/LocationModalTreeHeader";
import CreateEditLocationModal from "./CreateEditLocationModal";
import EditTenantModal from '../../components/Modals/EditTenantModal';
import BulkDeleteModalViewModel from '../../pages/HierachyManagementDashboard/BulkDeleteModalViewModel';
import BulkMoveModal from "./BulkMoveModal.js";

const HierarchyManagementDashboardPage = ({ hierarchyData, expandRoot, error }) => {
	
	const [checkedItems, setCheckedItems] = useState([]);
	const [disabledItems, setDisabledItems] = useState([]);
	const [isTenantModalShowing, setIsTenantModalShowing] = useState();
	const [tenantAccountId, setTenantAccountId] = useState();
	const [isDeleteModalShowing, setIsDeleteModalShowing] = useState();
	const [isMoveModalShowing, setIsMoveModalShowing] = useState(false);
	const [toast, SetToast] = useState();
	const allowedAccountTypesForMove = [StructuralAccountType.Tenant, StructuralAccountType.Suborg]
	

	const toastToggleCallback = () => {
		SetToast({
			display: false,
			message: ""
		});
	};
	const makeToast = (message, type) => {
		SetToast({
			display: true,
			message: message,
			type: type
		});
	};


	const { modalTreeCheckedItem, setModalTreeCheckedItem, modalTreeExpandedItems, setModalTreeExpandedItems, expandedItems, setExpandedItems, locationModal } = useContext(LocationModalContext);
	
	useEffect(() => {
		setExpandedItems(expandRoot)
	}, []);

    const suborgModalReducer = (previousState, action) => {
        switch (action.type) {
            case 'OpenCreate':
                return {
                    ...previousState,
                    isOpen: true,
                    accountId: null,
                    parentId: action.parentId,
                    name: null
                };
            case 'OpenEdit':
                return {
                    ...previousState,
                    isOpen: true,
                    accountId: action.accountId,
                    parentId: action.parentId,
                    name: action.name,
                };
            case 'Close':
                return {
                    ...previousState,
                    isOpen: false,
                    parentId: null,
                    accountId: null,
                    name: null
                };
            default:
                return {
                    ...previousState
                }
        }
    };

	const [orgUpdateState, orgUpdateStateDispatcher] = useReducer(suborgModalReducer, { isOpen: false, parentId: null, id: null });

	useEffect(() => { 
		setModalTreeExpandedItems(expandedItems);
	}, [expandedItems, setModalTreeExpandedItems]);
	
	const hierarchyDataWithoutLocation = useMemo(() => {
		return hierarchyData ? removeNodesByType(hierarchyData, StructuralAccountType.Location) : null;
	}, [hierarchyData]);
	
    const handleRenderNode = (node) => {
		return <TreeNode
			showActionButtons={true}
			node={node}
			treeSize={treeTypes.FullSize}
			handleCheckboxClick={handleCheckboxClick}
			onAddOrgClick={addOrgClickHandler}
			onEditOrgClick={editOrgClickHandler}
			onEditTenantClick={editTenantClickHandler}
			selectAll={selectAll}
			deselectAll={deselectAll}
		/>;
    };

	const handleModalTreeRenderNode = (node) => {
		return <TreeNode node={node}
			treeSize={treeTypes.Mini}
			showActionButtons={false}
			handleCheckboxClick={handleModalTreeCheckboxClick}
		/>;
	};

	const handleExpand = (item) => {
		setExpandedItems([...expandedItems, item.index]);
	};
	const handleExpandModalTree = (item) => {
		setModalTreeExpandedItems((prevState) => [...prevState, item.index]);
	};

	const handleCollapse = (item) => {
		setExpandedItems(
			expandedItems.filter(
				(expandedItemIndex) => expandedItemIndex !== item.index
			)
		);
	};
	const handleCollapseModalTree = (item) => {
		setModalTreeExpandedItems((prevState) => {
			return prevState.filter(
				(expandedItemIndex) => expandedItemIndex !== item.index
			)
		});
	};
	const onLocationModalClose = () => {
		//Resets the mini-tree expanded items to the current expanded items of the main tree on location modal close
		setModalTreeExpandedItems(expandedItems);
	}

	const getNodeLabel = (item) => {
		return `${item.data.name} (${item.data.accountId})`;
	};

	const setSelfAndDescendantsAsChecked = (accountId) => {
		const descendantIds = getDescendantNodes(accountId);
		
		// existing checked items + checked item + checked item's descendants
		setCheckedItems([
			...checkedItems,
			accountId,
			...descendantIds.filter((i) => !checkedItems.some(s => s === i))]);
		setDisabledItems([...disabledItems, ...descendantIds]);
	};

	const setSelfAndNextLayerAsUnchecked = (accountId) => {
		// Grab just the next layer of nodes, not recursive
		const childIds = getDescendantNodes(accountId, false);
		const idsToUncheck = [accountId, ...childIds];
		const idsToUncheckSet = new Set(idsToUncheck);

		// uncheck clicked checkbox
		setCheckedItems([...checkedItems.filter((i) => i !== accountId)]);
		// re-enable + checked item + checked item's immediate descendants
		setDisabledItems([...disabledItems.filter((i) => !idsToUncheckSet.has(i))]);
	};

	const getDescendantNodes = (parentId, recursive = true) => {
		const fullNodeData = hierarchyData[parentId];

		if (!fullNodeData) return [];

		let descendants = fullNodeData.children;

		if (recursive && descendants) {
			for (const child of descendants) {
				descendants = [...descendants, ...getDescendantNodes(child)];
			}
		}

		return descendants;
	};

	const handleCheckboxClick = (node) => {
		const isChecked = node.context.viewStateFlags.checkedItems;
		const accountId = node.item.accountId;

		if (isChecked) {
			setSelfAndNextLayerAsUnchecked(accountId);
		} else {
			setSelfAndDescendantsAsChecked(accountId);
		}
	};
	const handleModalTreeCheckboxClick = (node) => {
		setModalTreeCheckedItem([node.item.accountId]);
	}

	const selectAll = () => {
		const parentId = hierarchyData.root.children[0];
		const parentDescendants = getDescendantNodes(parentId, false);

		//can't use setSelfAndDescendantsAsChecked becuase it would create a race condition with setState
		let childDescendants = [];
		for (const child of parentDescendants) {
			childDescendants = [...childDescendants, ...getDescendantNodes(child)];
		}

		setCheckedItems([...childDescendants, ...parentDescendants]);
		setDisabledItems(childDescendants);
	};

	const deselectAll = () => {
		setCheckedItems([]);
		setDisabledItems([]);
	};

	const deleteSelected = () => {
		setIsDeleteModalShowing(true);
	};

	function moveSelected(){
		setIsMoveModalShowing(true);
	}

	const addOrgClickHandler = (parentId) => {
		orgUpdateStateDispatcher({ type: 'OpenCreate', parentId });
	}

	const editOrgClickHandler = (accountId, parentId, name) => {
		orgUpdateStateDispatcher({ type: 'OpenEdit', parentId, accountId, name });
	}

	const orgModalCloseHandler = () => {
		orgUpdateStateDispatcher({ type: 'Close' });
	}
	const editTenantClickHandler = (node) => {
		setTenantAccountId(node.item.accountId);
		setIsTenantModalShowing(true);
	}
	const editTenantModalToggle = () => {
		setIsTenantModalShowing(!isTenantModalShowing);
	}

	const deleteSelectedModalToggle = () => {
		setIsDeleteModalShowing(!isDeleteModalShowing);
	}

	const updateTreePostDelete = async () => {
		setIsDeleteModalShowing(false);
	}

	async function handleMoveAccounts(destination){
		deselectAll();
		setIsMoveModalShowing(false);
	}

	const treeProps = {
		hierarchyData,
		handleRenderNode,
		getNodeLabel,
		handleExpand,
		handleCollapse,
		expandedItems,
		disabledItems,
		checkedItems
	};

	return (
		<>
			{toast?.display && (
				<ToastMessage Message={toast?.message} Type={toast?.type} ToggleCallback={toastToggleCallback} />
			)}
			<SimpleHeader name="Home" parentName="Location  Management" />
			<div>
				
				{hierarchyData && (
					<Container className="mt--6" fluid>
						<Row className="justify-content-center">
							<Col className="card-wrapper">
								<Card className="card-header-devices">
									<StructuralHierarchyTree {...treeProps}
										renderDepthOffset={35}
										treeId="main-tree"
										treeSize={treeTypes.FullSize}
										treeHeader={
											<MainTreeHeader
												checkedItems={checkedItems}
												deleteSelected={deleteSelected}
												moveSelected={moveSelected}
											/>}
									/>
								</Card>
								
							</Col>
						</Row>
					</Container>
				)}
				{error === treeState.NotFound && <h3>No Hierarchy Tree to Show</h3>}
			</div>
			{locationModal && <CreateEditLocationModal onClosed={onLocationModalClose} makeToast={makeToast}>
				<StructuralHierarchyTree {...treeProps}
					expandedItems={modalTreeExpandedItems}
					disabledItems={[]}
					handleExpand={handleExpandModalTree}
					handleCollapse={handleCollapseModalTree}
					checkedItems={modalTreeCheckedItem}
					hierarchyData={hierarchyDataWithoutLocation}
					handleRenderNode={handleModalTreeRenderNode}
					treeId="modal-tree"
					treeHeader={<LocationModalTreeHeader />}
					renderDepthOffset={30}
					treeSize={treeTypes.Mini}
					treeMaxHeight="50vh"
				/>
			</CreateEditLocationModal>}
			{isTenantModalShowing && <EditTenantModal
				accountId={tenantAccountId}></EditTenantModal>}

			<CreateEditOrganizationModalViewModel
				isOpen={orgUpdateState.isOpen}
				parentId={orgUpdateState.parentId}
				accountId={orgUpdateState.accountId}
				name={orgUpdateState.name}
				onComplete={orgModalCloseHandler}
				makeToast={makeToast} />
			{isDeleteModalShowing &&
				<BulkDeleteModalViewModel
				isOpen={isDeleteModalShowing}
				cancel={deleteSelectedModalToggle}
				checkedItems={checkedItems}
				data={treeProps.hierarchyData}
				updateTreePostDelete={updateTreePostDelete}
				makeToast={makeToast}
				deselectAll={deselectAll}
				/>}
			{isTenantModalShowing &&
				<EditTenantViewModel
				isOpen={isTenantModalShowing}
				accountId={tenantAccountId}
				toggle={editTenantModalToggle}
				makeToast={makeToast}
				/>}
			{isMoveModalShowing && 
				<BulkMoveModal 
				isOpen={isMoveModalShowing} 
				checkedItems={checkedItems}
				hierarchyData={treeProps.hierarchyData}
				allowedDestinationAccountTypeIds={allowedAccountTypesForMove}
				cancel={() => setIsMoveModalShowing(false)}
				handleMove={handleMoveAccounts}
				makeToast={makeToast}
				/>}

		</>
	);
};

HierarchyManagementDashboardPage.propTypes = {
	orgData: PropTypes.object,
};

export default HierarchyManagementDashboardPage;
